/**
 * Tablet client diagnostics phone-home helpers (#703).
 *
 * Collects device snapshot fields from the browser and exposes a small
 * `sendClientEvent` wrapper. Sending is done over the existing voice
 * WebSocket (see TabletApp.js's `client_diagnostics` + `client_event`
 * cases) rather than via REST, because the conversation row only
 * exists at end-of-call — REST would FK-fail mid-call.
 *
 * Every helper is best-effort: any failure (missing API on the
 * browser, denied permission, dead WS) is swallowed. The point of
 * this module is to ship diagnostic data when possible, never to
 * block or fail the call.
 */

/**
 * Read browser/network/permission/device fields and return them as a
 * single object ready for the server's `client_diagnostics` WS
 * message. Async because the mic-permission query and the device
 * enumeration are both async.
 *
 * @param {string} appVersion — REACT_APP_BUILD_SHA value
 * @returns {Promise<object>}
 */
export async function collectDeviceSnapshot(appVersion) {
  const snap = {
    user_agent: navigator.userAgent || null,
    viewport_w: window.innerWidth || null,
    viewport_h: window.innerHeight || null,
    app_version: appVersion || null,
  };

  // navigator.connection is Chromium-only at the time of writing
  // (Safari doesn't expose it). Probe defensively so this code never
  // throws on iPad.
  const conn = navigator.connection
    || navigator.mozConnection
    || navigator.webkitConnection;
  if (conn) {
    if (typeof conn.effectiveType === 'string') {
      snap.network_effective_type = conn.effectiveType;
    }
    if (typeof conn.downlink === 'number') {
      snap.network_downlink_mbps = conn.downlink;
    }
    if (typeof conn.rtt === 'number') {
      snap.network_rtt_ms = conn.rtt;
    }
  }

  try {
    if (navigator.permissions && typeof navigator.permissions.query === 'function') {
      // The microphone permission name is in the experimental Permissions
      // API spec; Safari sometimes throws TypeError. Catch and move on.
      const p = await navigator.permissions.query({ name: 'microphone' });
      if (p && typeof p.state === 'string') {
        snap.mic_permission = p.state;
      }
    }
  } catch (_) {
    // Either Safari (no microphone permission name) or denied access.
    // Snapshot stays best-effort.
  }

  try {
    if (
      navigator.mediaDevices
      && typeof navigator.mediaDevices.enumerateDevices === 'function'
    ) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      // Device labels are gated behind a granted microphone permission
      // on most browsers (otherwise they're empty strings). Reporting
      // empty strings is fine — they still mark the field as present.
      const input = devices.find((d) => d.kind === 'audioinput');
      const output = devices.find((d) => d.kind === 'audiooutput');
      if (input) snap.audio_input_device = input.label || '(no label)';
      if (output) snap.audio_output_device = output.label || '(no label)';
    }
  } catch (_) {
    // Some browsers throw on enumerateDevices() in insecure contexts.
  }

  return snap;
}

/**
 * Send a client-side phone-home event over the active voice WS.
 * Swallows failures (closed WS, JSON error). Best-effort signal.
 *
 * @param {WebSocket} ws — the active voice-call WebSocket
 * @param {string} eventType — reconnect | audio_context_suspend | client_error
 * @param {object} [payload] — small free-form payload
 */
export function sendClientEvent(ws, eventType, payload) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  try {
    ws.send(JSON.stringify({
      type: 'client_event',
      event_type: eventType,
      payload: payload || {},
    }));
  } catch (_) {
    // No-op — diagnostic POST mustn't impact the call.
  }
}

/**
 * Send a device-snapshot phone-home message over the active voice WS.
 * Swallows all failures.
 *
 * @param {WebSocket} ws — the active voice-call WebSocket
 * @param {object} snapshot — output of collectDeviceSnapshot
 */
export function sendClientDiagnostics(ws, snapshot) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  try {
    ws.send(JSON.stringify({
      type: 'client_diagnostics',
      snapshot: snapshot || {},
    }));
  } catch (_) {
    // No-op — diagnostic POST mustn't impact the call.
  }
}
